import { FC, memo, ReactElement } from 'react';
import { useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { useAppPagination } from '@/common/hooks';
import { BaseQueryParams } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import {
  SiteDomainsQueryKeys,
  useAddChangeMainDomainTaskMutation,
  useAddInSearchConsoleDomainMutation,
  useChangeMainDomainProcessStatus,
  useDenyAccessSiteDomainMutation,
  useGenerateGoogleTokenDomainMutation,
  useGetAllSiteDomainsQuery,
  useGetByIdSiteDomainsQuery,
  usePurgeCacheSiteDomainMutation,
  useUpdateSCAccountProcessStatus,
  useVerifyGoogleDomainMutation,
} from '@/modules/Domains';
import { useGetContentByIdSitesQuery } from '@/modules/Sites';

import { SiteDomainsDossierView } from './SiteDomainsDossierView';

interface Props {
  siteId: string;
  domainId: string;
}

export const SiteDomainsDossierContainer: FC<Props> = memo(({ siteId, domainId }) => {
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const { pageParam: currentPage } = useAppPagination();

  const {
    data: siteData,
    error: siteError,
    isError: isSiteError,
    isLoading: isSiteLoading,
  } = useGetContentByIdSitesQuery(siteId)({
    enabled: !!siteId,
  });

  const {
    data: siteDomainData,
    isError: isSiteDomainError,
    isLoading: isSiteDomainLoading,
    error: siteDomainError,
  } = useGetByIdSiteDomainsQuery(siteId, domainId)({ enabled: !!siteId && !!domainId });

  const { mutate: changeSitesMainDomain, isLoading: isChangeSitesMainDomainLoading } = useAddChangeMainDomainTaskMutation(
    siteId,
    domainId,
  )({
    onError: (error) => {
      messageOnError(error, appMessageApi);
    },
  });

  const { mutate: verifyDomainMutation, isLoading: isVerifyDomainLoading } = useVerifyGoogleDomainMutation(
    siteId,
    domainId,
  )({
    onSuccess: ({ isGoogleVerificated, googleVerificationErrorMessage }) => {
      queryClient.setQueryData<Partial<BaseQueryParams<typeof useGetByIdSiteDomainsQuery>['data']>>(
        [SiteDomainsQueryKeys.GetByIdDomains, siteId, domainId],
        (data) => {
          return { ...data, isGoogleVerificated, googleVerificationErrorMessage };
        },
      );

      queryClient.setQueryData<Partial<BaseQueryParams<typeof useGetAllSiteDomainsQuery>['data']>>(
        [SiteDomainsQueryKeys.GetAllDomains, siteId, { page: currentPage }],
        (data) => {
          return {
            ...data,
            items: data?.items?.some(({ id }) => id === domainId)
              ? data.items.map((item) => (item.id === domainId ? { ...item, isGoogleVerificated } : item))
              : data?.items,
          };
        },
      );

      appMessageApi.success('Верифікацію домену було перевірено!');
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    },
  });

  const { mutate: addDomainToSearchConsoleMutation, isLoading: isAddDomainLoading } = useAddInSearchConsoleDomainMutation(
    siteId,
    domainId,
  )({
    onSuccess: ({ isIncludedInSearchConsole }) => {
      queryClient.setQueryData<Partial<BaseQueryParams<typeof useGetByIdSiteDomainsQuery>['data']>>(
        [SiteDomainsQueryKeys.GetByIdDomains, siteId, domainId],
        (data) => {
          return { ...data, isIncludedInSearchConsole };
        },
      );

      appMessageApi.success('Домен було додано в Search Console!');
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    },
  });

  const { mutate: generateTokenMutation, isLoading: isGenerateTokenLoading } = useGenerateGoogleTokenDomainMutation(
    siteId,
    domainId,
  )({
    onSuccess: ({ googleVerificationToken }) => {
      queryClient.setQueryData<Partial<BaseQueryParams<typeof useGetByIdSiteDomainsQuery>['data']>>(
        [SiteDomainsQueryKeys.GetByIdDomains, siteId, domainId],
        (data) => {
          return { ...data, googleVerificationToken };
        },
      );

      appMessageApi.success('Домен було додано в Search Console!');
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    },
  });

  const { mutate: denyDomainAccessMutation, isLoading: isDenyDomainAccessLoading } = useDenyAccessSiteDomainMutation(
    siteId,
    domainId,
  )({
    onSuccess: () => {
      queryClient.setQueryData<Partial<BaseQueryParams<typeof useGetByIdSiteDomainsQuery>['data']>>(
        [SiteDomainsQueryKeys.GetByIdDomains, siteId, domainId],
        (data) => {
          return { ...data, isAccessDenied: !siteDomainData?.isAccessDenied };
        },
      );

      appMessageApi.success('Налаштування доступу було змінено успішно!');
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    },
  });

  const { mutate: purgeDomainCacheMutation, isLoading: isPurgeDomainCacheLoading } = usePurgeCacheSiteDomainMutation(
    siteId,
    domainId,
  )({
    onSuccess: ({ lastCachePurgedAt }) => {
      queryClient.setQueryData<Partial<BaseQueryParams<typeof useGetByIdSiteDomainsQuery>['data']>>(
        [SiteDomainsQueryKeys.GetByIdDomains, siteId, domainId],
        (data) => {
          return { ...data, lastCachePurgedAt };
        },
      );

      appMessageApi.success('Кеш домену було скинуто успішно!');
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    },
  });

  useChangeMainDomainProcessStatus({ siteId, domainId });

  useUpdateSCAccountProcessStatus({ siteId, domainId });

  return (
    <FallbackedLayout
      data={siteDomainData}
      error={siteDomainError || siteError}
      isError={isSiteDomainError || isSiteError}
      isLoading={isSiteDomainLoading || isSiteLoading}
    >
      {(siteDomain): ReactElement => (
        <SiteDomainsDossierView
          siteId={siteId}
          domainId={domainId}
          siteDomain={siteDomain}
          isChangeSitesMainDisabled={isChangeSitesMainDomainLoading}
          changeMainDomainProcessStatus={siteData?.changeMainDomainProcessStatus}
          isVerifyDomainLoading={isVerifyDomainLoading}
          isAddDomainLoading={isAddDomainLoading}
          isGenerateTokenLoading={isGenerateTokenLoading}
          isDenyDomainAccessLoading={isDenyDomainAccessLoading}
          isPurgeDomainCacheLoading={isPurgeDomainCacheLoading}
          handleChangeSitesMain={changeSitesMainDomain}
          handleCheckGoogleVerification={verifyDomainMutation}
          handleAddToSearchConsole={addDomainToSearchConsoleMutation}
          handleGenerateGoogleToken={generateTokenMutation}
          handleDenyDomainAccess={denyDomainAccessMutation}
          handlePurgeDomainCache={purgeDomainCacheMutation}
        />
      )}
    </FallbackedLayout>
  );
});
